import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtRp, todayISO } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const emptyLine = () => ({ account_id: "", debit: "", credit: "" });

export function JournalForm({ open, onClose, onSaved, item, accounts }) {
  const [f, setF] = useState({ date: todayISO(), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(item ? { date: item.date, description: item.description, reference: item.reference || "",
      lines: item.lines.map((l) => ({ account_id: l.account_id, debit: l.debit || "", credit: l.credit || "" })) }
      : { date: todayISO(), description: "", reference: "", lines: [emptyLine(), emptyLine()] });
  }, [open, item]);

  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, idx) => (idx === i ? { ...l, [k]: v, ...(k === "debit" && v ? { credit: "" } : {}), ...(k === "credit" && v ? { debit: "" } : {}) } : l)) }));
  const td = f.lines.reduce((a, l) => a + Number(l.debit || 0), 0);
  const tc = f.lines.reduce((a, l) => a + Number(l.credit || 0), 0);
  const balanced = td > 0 && Math.abs(td - tc) < 0.01;
  const valid = balanced && f.description && f.date && f.lines.every((l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...f, lines: f.lines.map((l) => ({ account_id: l.account_id, debit: Number(l.debit || 0), credit: Number(l.credit || 0) })) };
    try {
      if (item) await api.put(`/accounting/journals/${item.id}`, payload); else await api.post("/accounting/journals", payload);
      toast.success("Jurnal disimpan"); onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl rounded-2xl" data-testid="journal-form-dialog">
        <DialogHeader><DialogTitle>{item ? `Edit Jurnal ${item.entry_no}` : "Entri Jurnal Umum"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required data-testid="input-journal-date" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Deskripsi</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required placeholder="cth. Pembayaran gaji karyawan Juni" data-testid="input-journal-desc" /></div>
          </div>
          <div className="space-y-1.5"><Label>Referensi</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="No. bukti / invoice (opsional)" data-testid="input-journal-ref" /></div>

          <div className="rounded-xl border border-sky-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="table-head"><tr><th className="text-left px-3 py-2">Akun</th><th className="text-right px-3 py-2 w-40">Debit (Rp)</th><th className="text-right px-3 py-2 w-40">Kredit (Rp)</th><th className="w-10" /></tr></thead>
              <tbody>
                {f.lines.map((l, i) => (
                  <tr key={i} className="border-t border-sky-50" data-testid={`journal-line-${i}`}>
                    <td className="px-2 py-1.5">
                      <Select value={l.account_id} onValueChange={async (v) => {
                        if (v !== "__new__") return setLine(i, "account_id", v);
                        const name = window.prompt("Nama akun baru (tipe Beban):"); if (!name) return;
                        try { const r = await api.post("/accounting/accounts/quick", { name, type: "beban" }); accounts.push(r.data); setLine(i, "account_id", r.data.id); toast.success(`Akun ${r.data.code} ${r.data.name} dibuat`); } catch (e) { toast.error(errMsg(e)); }
                      }}>
                        <SelectTrigger className="h-9" data-testid={`select-journal-account-${i}`}><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                        <SelectContent className="max-h-72"><SelectItem value="__new__" className="text-sky-700 font-semibold">+ Tambah akun baru</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}><span className="font-mono text-xs text-slate-500 mr-2">{a.code}</span>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5"><MoneyInput className="h-9 text-right" value={l.debit} onChange={(v) => setLine(i, "debit", v)} data-testid={`input-journal-debit-${i}`} /></td>
                    <td className="px-2 py-1.5"><MoneyInput className="h-9 text-right" value={l.credit} onChange={(v) => setLine(i, "credit", v)} data-testid={`input-journal-credit-${i}`} /></td>
                    <td className="px-1"><Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600" disabled={f.lines.length <= 2} onClick={() => setF({ ...f, lines: f.lines.filter((_, idx) => idx !== i) })} data-testid={`btn-remove-line-${i}`}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-sky-50/70 border-t border-sky-100 font-semibold">
                <tr>
                  <td className="px-3 py-2"><Button type="button" variant="ghost" size="sm" className="text-sky-700 h-8" onClick={() => setF({ ...f, lines: [...f.lines, emptyLine()] })} data-testid="btn-add-line"><Plus className="h-4 w-4 mr-1" /> Tambah baris</Button></td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="journal-total-debit">{fmtRp(td)}</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="journal-total-credit">{fmtRp(tc)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className={`text-xs font-semibold px-3 py-2 rounded-lg ${balanced ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="journal-balance-status">
            {balanced ? "Seimbang — debit = kredit" : `Belum seimbang — selisih ${fmtRp(Math.abs(td - tc))}`}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving || !valid} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-journal-submit">{saving ? "Menyimpan..." : "Simpan Jurnal"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
